import React, { useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import {
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
} from "firebase/auth";
import { auth } from "../../config/firebase";
import "./SettingsPage.css";

const SettingsPage: React.FC = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("account");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  // Password change state
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  
  // Check if user is using email/password authentication
  const [isPasswordAuth, setIsPasswordAuth] = useState(false);
  
  React.useEffect(() => {
    const currentUser = auth.currentUser;
    if (currentUser) {
      // Check if user has password provider (email/password authentication)
      const hasPasswordProvider = currentUser.providerData.some(
        (provider) => provider.providerId === "password"
      );
      setIsPasswordAuth(hasPasswordProvider);
    }
  }, [user]);

  if (!user) {
    return (
      <div className="settings-page">
        <div className="settings-container">
          <h2>Settings</h2>
          <p>Please log in to access settings.</p>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    setLoading(true);
    try {
      await logout();
      setMessage("Logged out successfully!");
    } catch (error) {
      setMessage("Failed to log out. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordError("");
    setMessage("");

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError("Please fill in all password fields");
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError("New password must be at least 6 characters long");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("New passwords do not match");
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError("New password must be different from current password");
      return;
    }

    setPasswordLoading(true);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser || !currentUser.email) {
        throw new Error("No user is currently signed in");
      }

      // Re-authenticate user before changing password
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);

      // Update password
      await updatePassword(currentUser, newPassword);

      // Success
      setMessage("Password changed successfully!");
      setShowPasswordModal(false);
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (error: any) {
      console.error("Password change error:", error);

      let errorMessage = "Failed to change password. Please try again.";

      if (error.code === "auth/wrong-password") {
        errorMessage = "Current password is incorrect";
      } else if (error.code === "auth/weak-password") {
        errorMessage = "New password is too weak";
      } else if (error.code === "auth/requires-recent-login") {
        errorMessage =
          "For security, please sign out and sign in again before changing your password";
      } else if (error.message) {
        errorMessage = error.message;
      }

      setPasswordError(errorMessage);
    } finally {
      setPasswordLoading(false);
    }
  };

  const getSubscriptionLimits = () => {
    switch (user.subscriptionTier) {
      case "free":
        return { maps: 100, overlays: 100, publicMaps: false };
      case "premium":
        return { maps: 50, overlays: 25, publicMaps: true };
      case "enterprise":
        return { maps: -1, overlays: -1, publicMaps: true }; // Unlimited
      default:
        return { maps: 100, overlays: 100, publicMaps: false };
    }
  };

  const limits = getSubscriptionLimits();

  return (
    <div className="settings-page">
      <div className="settings-container">
        <div className="settings-header">
          <h2>Settings</h2>
        </div>

        {message && (
          <div
            className={`message ${
              message.includes("successfully") ? "success" : "error"
            }`}
          >
            {message}
          </div>
        )}

        <div className="settings-content">
          <div className="settings-sidebar">
            <button
              className={`tab-button ${
                activeTab === "account" ? "active" : ""
              }`}
              onClick={() => setActiveTab("account")}
            >
              Account
            </button>
            <button
              className={`tab-button ${
                activeTab === "subscription" ? "active" : ""
              }`}
              onClick={() => setActiveTab("subscription")}
            >
              Subscription
            </button>
            <button
              className={`tab-button ${
                activeTab === "privacy" ? "active" : ""
              }`}
              onClick={() => setActiveTab("privacy")}
            >
              Privacy
            </button>
            <button
              className={`tab-button ${
                activeTab === "security" ? "active" : ""
              }`}
              onClick={() => setActiveTab("security")}
            >
              Security
            </button>
          </div>

          <div className="settings-main">
            {activeTab === "account" && (
              <div className="settings-section">
                <h3>Account Settings</h3>
                <div className="setting-item">
                  <div className="setting-info">
                    <h4>Email</h4>
                    <p>{user.email}</p>
                  </div>
                  <button className="secondary-button">Change Email</button>
                </div>

                {isPasswordAuth && (
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Password</h4>
                      <p>Keep your account secure with a strong password</p>
                    </div>
                    <button
                      className="secondary-button"
                      onClick={() => setShowPasswordModal(true)}
                    >
                      Change Password
                    </button>
                  </div>
                )}
                
                {!isPasswordAuth && (
                  <div className="setting-item">
                    <div className="setting-info">
                      <h4>Password</h4>
                      <p>
                        You signed in with Google. Your password is managed by
                        Google.
                      </p>
                    </div>
                  </div>
                )}

                <div className="setting-item">
                  <div className="setting-info">
                    <h4>Account Deletion</h4>
                    <p>Permanently delete your account and all data</p>
                  </div>
                  <button className="danger-button">Delete Account</button>
                </div>
              </div>
            )}

            {activeTab === "subscription" && user && (
              <div className="settings-section">
                <h3>Subscription & Limits</h3>

                <div className="subscription-card">
                  <div className="subscription-header">
                    <h4>
                      {user.subscriptionTier.charAt(0).toUpperCase() +
                        user.subscriptionTier.slice(1)}{" "}
                      Plan
                    </h4>
                    <span className="subscription-status">Active</span>
                  </div>

                  <div className="limits-grid">
                    <div className="limit-item">
                      <span className="limit-label">Maps Created</span>
                      <span className="limit-value">
                        {user.mapsCount} /{" "}
                        {limits.maps === -1 ? "∞" : limits.maps}
                      </span>
                    </div>
                    <div className="limit-item">
                      <span className="limit-label">Overlays</span>
                      <span className="limit-value">
                        {user.mapsCount} /{" "}
                        {limits.overlays === -1 ? "∞" : limits.overlays}
                      </span>
                    </div>
                    <div className="limit-item">
                      <span className="limit-label">Public Maps</span>
                      <span className="limit-value">
                        {limits.publicMaps ? "✓ Allowed" : "✗ Not Allowed"}
                      </span>
                    </div>
                  </div>

                  {/* Upgrade prompt hidden - keeping subscriptions loose */}
                </div>
              </div>
            )}

            {activeTab === "privacy" && (
              <div className="settings-section">
                <h3>Privacy Settings</h3>

                <div className="setting-item">
                  <div className="setting-info">
                    <h4>Profile Visibility</h4>
                    <p>Control who can see your profile and maps</p>
                  </div>
                  <select className="setting-select">
                    <option value="public">Public</option>
                    <option value="followers">Followers Only</option>
                    <option value="private">Private</option>
                  </select>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <h4>Map Discovery</h4>
                    <p>Allow your maps to appear in search results</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <h4>Email Notifications</h4>
                    <p>Receive email notifications for activity</p>
                  </div>
                  <label className="toggle-switch">
                    <input type="checkbox" defaultChecked />
                    <span className="toggle-slider"></span>
                  </label>
                </div>
              </div>
            )}

            {activeTab === "security" && (
              <div className="settings-section">
                <h3>Security Settings</h3>

                <div className="setting-item">
                  <div className="setting-info">
                    <h4>Two-Factor Authentication</h4>
                    <p>Add an extra layer of security to your account</p>
                  </div>
                  <button className="secondary-button">Enable 2FA</button>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <h4>Active Sessions</h4>
                    <p>Manage your active login sessions</p>
                  </div>
                  <button className="secondary-button">View Sessions</button>
                </div>

                <div className="setting-item">
                  <div className="setting-info">
                    <h4>Logout</h4>
                    <p>Sign out of your current session</p>
                  </div>
                  <button
                    className="danger-button"
                    onClick={handleLogout}
                    disabled={loading}
                  >
                    {loading ? "Signing out..." : "Sign Out"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Password Change Modal */}
        {showPasswordModal && (
          <div className="modal-overlay" onClick={() => setShowPasswordModal(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Change Password</h3>
                <button
                  className="modal-close"
                  onClick={() => {
                    setShowPasswordModal(false);
                    setPasswordError("");
                    setCurrentPassword("");
                    setNewPassword("");
                    setConfirmPassword("");
                  }}
                >
                  ×
                </button>
              </div>

              <form onSubmit={handlePasswordChange}>
                {passwordError && (
                  <div className="error-message">{passwordError}</div>
                )}

                <div className="form-group">
                  <label htmlFor="currentPassword">Current Password</label>
                  <input
                    type="password"
                    id="currentPassword"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter your current password"
                    disabled={passwordLoading}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">New Password</label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 characters)"
                    disabled={passwordLoading}
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm New Password</label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Confirm new password"
                    disabled={passwordLoading}
                    required
                  />
                </div>

                <div className="modal-actions">
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={() => {
                      setShowPasswordModal(false);
                      setPasswordError("");
                      setCurrentPassword("");
                      setNewPassword("");
                      setConfirmPassword("");
                    }}
                    disabled={passwordLoading}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="primary-button"
                    disabled={passwordLoading}
                  >
                    {passwordLoading ? "Changing..." : "Change Password"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
